import { Controller } from '@hotwired/stimulus'
import Matter from 'matter-js'

/**
 * Путь к спрайту мяча для Matter.js.
 *
 * PNG используется намеренно: Matter корректно отрисовывает его без
 * артефактов прозрачности.
 *
 * @type {string}
 */
const ballTextureSrc = '/images/ball.png'

/**
 * Задержка перед пересозданием физической сцены после изменения размера footer.
 *
 * @type {number}
 */
const resizeDebounceMs = 300

/**
 * Толщина невидимых стен вокруг сцены.
 *
 * @type {number}
 */
const wallSize = 100

const {
  MouseConstraint,
  Mouse,
  Composite,
  Engine,
  Common,
  Bodies,
  Render,
  Runner,
  Query
} = Matter

const deviceConfig = {
  desktop: {
    ballSizeMultiplier: 5,
    ballColumnsMultiplier: 1.5,
    gravityScale: 0.0022
  },
  mobile: {
    ballSizeMultiplier: 2.25,
    ballColumnsMultiplier: 1.5,
    gravityScale: 0.001
  }
}

let ballTexturePromise = null

/**
 * Возвращает закешированный промис загрузки текстуры мяча.
 *
 * @returns {Promise<HTMLImageElement>} Загруженное изображение мяча.
 */
const getBallTexture = () => {
  ballTexturePromise ||= loadImage(ballTextureSrc)
  return ballTexturePromise
}

/**
 * Асинхронно загружает изображение и дожидается его декодирования, если
 * браузер поддерживает `HTMLImageElement.decode`.
 *
 * Ошибка загрузки не прерывает запуск анимации: Matter сможет продолжить
 * работу, даже если спрайт не был получен.
 *
 * @param {string} src - URL изображения.
 * @returns {Promise<HTMLImageElement>} Экземпляр изображения.
 */
const loadImage = async (src) => {
  const image = new Image()
  image.decoding = 'async'

  const loaded = new Promise((resolve) => {
    image.onload = () => resolve(image)
    image.onerror = () => resolve(image)
  })

  image.src = src

  if (image.decode) {
    try {
      await image.decode()
    } catch {
      await loaded
    }
  } else if (!image.complete) {
    await loaded
  }

  return image
}

/**
 * Stimulus-контроллер физической анимации мячей в footer.
 *
 * Сцена лениво запускается при первом появлении footer во viewport,
 * пересоздаётся при изменении размеров контейнера и полностью очищается при
 * отключении контроллера.
 */
export default class FooterAnimationController extends Controller {
  #reducedMotionMediaQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  )
  #context = null
  #hasStarted = false
  #intersectionObserver = null
  #isVisible = false
  #mobileMediaQuery = window.matchMedia('(max-width: 767px)')
  #resizeObserver = null
  #resizeTimeout = null
  #size = null

  /**
   * Инициализирует наблюдатели размера и видимости footer.
   *
   * @returns {void}
   */
  connect() {
    if (this.#reducedMotionMediaQuery.matches) return

    this.#size = this.#getSize()
    this.#resizeObserver = new ResizeObserver(() => this.#queueRestart())
    this.#resizeObserver.observe(this.element)

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => this.#handleIntersection(entries),
      { threshold: 0.4 }
    )
    this.#intersectionObserver.observe(this.element)
  }

  /**
   * Очищает таймеры, observer'ы, Matter.js context и DOM-слушатели.
   *
   * @returns {void}
   */
  disconnect() {
    clearTimeout(this.#resizeTimeout)
    this.#resizeObserver?.disconnect()
    this.#intersectionObserver?.disconnect()

    this.#resizeObserver = null
    this.#intersectionObserver = null
    this.#resizeTimeout = null
    this.#hasStarted = false
    this.#isVisible = false

    this.#destroyContext()
  }

  /**
   * Обновляет состояние видимости footer и запускает анимацию, если footer
   * оказался во viewport.
   *
   * При выходе из viewport анимация ставится на паузу, а обработчики
   * взаимодействия отключаются, чтобы mouse/touch события не запускали
   * проверки попадания по шарам вне видимой зоны.
   *
   * @param {IntersectionObserverEntry[]} entries - Записи IntersectionObserver.
   * @returns {void}
   */
  #handleIntersection(entries) {
    const entry = entries[0]
    this.#isVisible = entry?.isIntersecting || false

    if (!this.#isVisible) {
      this.#disableInteractions()
      this.#pauseAnimation()
      return
    }

    this.#enableInteractions()
    this.#startAnimation()
  }

  /**
   * Создаёт или возобновляет физическую сцену.
   *
   * @returns {void}
   */
  #startAnimation() {
    if (this.#reducedMotionMediaQuery.matches) return

    this.#hasStarted = true

    if (!this.#size?.width || !this.#size?.height) {
      this.#size = this.#getSize()
    }

    if (!this.#size.width || !this.#size.height) return

    if (this.#context) {
      this.#resumeAnimation()
      return
    }

    const context = this.#createContext()
    this.#context = context
    void this.#start(context)
  }

  /**
   * Дожидается текстуры мяча, создаёт renderer и runner, затем запускает сцену.
   *
   * Проверка актуальности context нужна, чтобы старый async-start не ожил после
   * resize или disconnect.
   *
   * @param {Object} context - Текущий Matter.js context сцены.
   * @returns {Promise<void>}
   */
  async #start(context) {
    const ballTextureImage = await getBallTexture()

    if (!this.#isCurrentContext(context)) return

    const render = Render.create({
      element: this.element,
      engine: context.engine,
      options: {
        width: context.width,
        height: context.height,
        pixelRatio: 'auto',
        showSleeping: false,
        wireframes: false,
        background: 'rgba(255, 255, 255, 0)',
        wireframeBackground: 'rgba(255, 255, 255, 0)'
      }
    })

    render.canvas.classList.add('footer__animation-canvas')
    render.textures[ballTextureSrc] = ballTextureImage

    context.render = render
    context.runner = Runner.create()
    this.#initMouse(context)
    this.#enableInteractions(context)
    this.#resumeAnimation()
  }

  /**
   * Запускает renderer и runner, если context готов, footer видим и анимация
   * ещё не работает.
   *
   * @returns {void}
   */
  #resumeAnimation() {
    const context = this.#context

    if (
      !this.#isVisible ||
      !context?.render ||
      !context.runner ||
      context.isRunning
    ) {
      return
    }

    Render.run(context.render)
    Runner.run(context.runner, context.engine)
    context.isRunning = true
  }

  /**
   * Останавливает RAF-циклы renderer и runner, оставляя сцену готовой к resume.
   *
   * @returns {void}
   */
  #pauseAnimation() {
    const context = this.#context

    if (!context?.render || !context.runner || !context.isRunning) {
      return
    }

    Render.stop(context.render)
    Runner.stop(context.runner)
    context.isRunning = false
  }

  /**
   * Полностью уничтожает физическую сцену, canvas и связанные обработчики.
   *
   * @returns {void}
   */
  #destroyContext() {
    const context = this.#context
    if (!context) return

    this.#disableInteractions(context)
    context.isDestroyed = true

    if (context.render) {
      Render.stop(context.render)
      context.render.canvas.remove()
      context.render.textures = {}
      context.render = null
    }

    if (context.runner) {
      Runner.stop(context.runner)
      context.runner = null
    }

    Composite.clear(context.engine.world, false)
    Engine.clear(context.engine)

    this.#context = null
  }

  /**
   * Планирует пересоздание сцены после изменения размера footer.
   *
   * Полное пересоздание нужно, чтобы заново рассчитать размеры мячей,
   * количество тел и границы физического мира.
   *
   * @returns {void}
   */
  #queueRestart() {
    const size = this.#getSize()

    if (!size.width || !size.height) return

    if (
      this.#size?.width === size.width &&
      this.#size?.height === size.height
    ) {
      return
    }

    this.#size = size
    clearTimeout(this.#resizeTimeout)
    this.#resizeTimeout = setTimeout(() => {
      if (this.#context) this.#destroyContext()
      if (this.#hasStarted && this.#isVisible) this.#startAnimation()
    }, resizeDebounceMs)
  }

  /**
   * Возвращает текущий размер DOM-элемента footer animation frame.
   *
   * @returns {{width: number, height: number}} Размеры элемента в CSS-пикселях.
   */
  #getSize() {
    return {
      width: Math.round(this.element.clientWidth),
      height: Math.round(this.element.clientHeight)
    }
  }

  /**
   * Создаёт новый Matter.js context: engine, стены, мячи и служебное состояние.
   *
   * @returns {Object} Context физической сцены.
   */
  #createContext() {
    const config = this.#mobileMediaQuery.matches
      ? deviceConfig.mobile
      : deviceConfig.desktop
    const { width, height } = this.#size
    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    )
    const ballSize = rootFontSize * config.ballSizeMultiplier
    const ballRadius = ballSize / 2
    const ballSpriteScale = ballSize / 250
    const walls = this.#createWalls(width, height)
    const ballColumns = Math.min(18, Math.max(6, Math.floor(width / ballSize)))
    const ballCount = Math.round(ballColumns * config.ballColumnsMultiplier)
    const balls = Array.from({ length: ballCount }, () =>
      Bodies.circle(
        Common.random(ballRadius, width - ballRadius),
        Common.random(-height, 0),
        ballRadius,
        {
          restitution: 0.7,
          friction: 1,
          frictionAir: 0.0001,
          frictionStatic: 0.5,
          density: 0.001,
          render: {
            sprite: {
              texture: ballTextureSrc,
              xScale: ballSpriteScale,
              yScale: ballSpriteScale
            }
          }
        }
      )
    )
    const engine = Engine.create({
      gravity: { scale: config.gravityScale }
    })

    Composite.add(engine.world, [...walls, ...balls])

    return {
      dynamicBodies: balls,
      engine,
      height,
      interactionsEnabled: false,
      isDestroyed: false,
      isRunning: false,
      mouse: null,
      mouseTarget: null,
      removeTouchListeners: null,
      render: null,
      runner: null,
      walls,
      width
    }
  }

  /**
   * Создаёт невидимые статичные стены, удерживающие мячи внутри footer.
   *
   * @param {number} width - Ширина сцены.
   * @param {number} height - Высота сцены.
   * @returns {Matter.Body[]} Массив тел-стен.
   */
  #createWalls(width, height) {
    const wideWall = width + wallSize * 2
    const tallWall = height + wallSize * 2
    const wallOptions = { isStatic: true, render: { visible: false } }

    return [
      [width / 2, height + wallSize / 2, wideWall, wallSize],
      [-wallSize / 2, height / 2, wallSize, tallWall],
      [width + wallSize / 2, height / 2, wallSize, tallWall]
    ].map((wall) => Bodies.rectangle(...wall, wallOptions))
  }

  /**
   * Настраивает Matter MouseConstraint и сохраняет данные для последующего
   * включения/выключения обработчиков взаимодействия.
   *
   * Canvas в CSS не принимает pointer events, поэтому desktop mouse слушается
   * через `body`, а touch — через `document` с ручной проверкой попадания в шар.
   *
   * @param {Object} context - Текущий Matter.js context сцены.
   * @returns {void}
   */
  #initMouse(context) {
    const { engine, render } = context
    const mouseTarget = this.element.ownerDocument.body
    const mouse = Mouse.create(mouseTarget)

    mouse.element = render.canvas
    mouse.pixelRatio = render.options.pixelRatio
    this.#removeMatterMouseElementListeners(mouse, mouseTarget)
    context.mouse = mouse
    context.mouseTarget = mouseTarget

    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: {
          visible: false
        }
      }
    })

    Composite.add(engine.world, mouseConstraint)

    render.mouse = mouse
    Mouse.setScale(mouse, {
      x: (render.bounds.max.x - render.bounds.min.x) / render.options.width,
      y: (render.bounds.max.y - render.bounds.min.y) / render.options.height
    })
    Mouse.setOffset(mouse, render.bounds.min)
  }

  /**
   * Включает обработчики взаимодействия с шарами, если footer сейчас видим.
   *
   * @param {Object} [context=this.#context] - Matter.js context сцены.
   * @returns {void}
   */
  #enableInteractions(context = this.#context) {
    if (
      !this.#isVisible ||
      !this.#isCurrentContext(context) ||
      context.interactionsEnabled ||
      !context.mouse ||
      !context.mouseTarget
    ) {
      return
    }

    this.#addMatterMouseElementListeners(context.mouse, context.mouseTarget)
    context.removeTouchListeners = this.#bindTouchMouse(context, context.mouse)
    context.interactionsEnabled = true
  }

  /**
   * Отключает обработчики взаимодействия с шарами.
   *
   * Используется, когда footer уходит из viewport, чтобы события мыши и touch
   * не запускали проверки попадания по шарам.
   *
   * @param {Object} [context=this.#context] - Matter.js context сцены.
   * @returns {void}
   */
  #disableInteractions(context = this.#context) {
    if (!this.#isCurrentContext(context) || !context.interactionsEnabled) {
      return
    }

    this.#removeMatterMouseElementListeners(context.mouse, context.mouseTarget)
    context.removeTouchListeners?.()
    context.removeTouchListeners = null
    context.mouse.button = -1
    context.interactionsEnabled = false
  }

  /**
   * Подключает touch-drag шаров и возвращает функцию очистки обработчиков.
   *
   * @param {Object} context - Текущий Matter.js context сцены.
   * @param {Matter.Mouse} mouse - Объект мыши Matter.js.
   * @returns {Function} Функция удаления touch-слушателей.
   */
  #bindTouchMouse(context, mouse) {
    const ownerDocument = this.element.ownerDocument
    let isDragging = false

    const stopDragging = (event) => {
      if (!isDragging) return

      isDragging = false
      mouse.mouseup(event)
      this.#stopTouchEvent(event)
      ownerDocument.removeEventListener('touchmove', handleMove, {
        capture: true
      })
      ownerDocument.removeEventListener('touchend', stopDragging, {
        capture: true
      })
      ownerDocument.removeEventListener('touchcancel', stopDragging, {
        capture: true
      })
    }

    const handleMove = (event) => {
      if (!isDragging) return

      mouse.mousemove(event)
      this.#stopTouchEvent(event)
    }

    const handleStart = (event) => {
      if (
        !this.#isVisible ||
        !this.#isCurrentContext(context) ||
        !this.#isTouchOnBody(context, event, mouse)
      ) {
        return
      }

      isDragging = true
      mouse.mousedown(event)
      this.#stopTouchEvent(event)

      ownerDocument.addEventListener('touchmove', handleMove, {
        capture: true,
        passive: false
      })
      ownerDocument.addEventListener('touchend', stopDragging, {
        capture: true,
        passive: false
      })
      ownerDocument.addEventListener('touchcancel', stopDragging, {
        capture: true,
        passive: false
      })
    }

    ownerDocument.addEventListener('touchstart', handleStart, {
      capture: true,
      passive: false
    })

    return () => {
      ownerDocument.removeEventListener('touchstart', handleStart, {
        capture: true
      })
      ownerDocument.removeEventListener('touchmove', handleMove, {
        capture: true
      })
      ownerDocument.removeEventListener('touchend', stopDragging, {
        capture: true
      })
      ownerDocument.removeEventListener('touchcancel', stopDragging, {
        capture: true
      })
    }
  }

  /**
   * Проверяет, попал ли touch в один из динамических мячей.
   *
   * @param {Object} context - Текущий Matter.js context сцены.
   * @param {TouchEvent} event - Touch-событие.
   * @param {Matter.Mouse} mouse - Объект мыши Matter.js.
   * @returns {boolean} `true`, если касание пришлось по мячу.
   */
  #isTouchOnBody(context, event, mouse) {
    const point = event.changedTouches?.[0]

    if (!point || !this.#isPointInsideCanvas(context, point)) {
      return false
    }

    const position = this.#getPointWorldPosition(context, point, mouse)
    const bodies = Query.point(context.dynamicBodies, position)

    return bodies.length > 0
  }

  /**
   * Проверяет, находится ли точка касания внутри canvas.
   *
   * @param {Object} context - Текущий Matter.js context сцены.
   * @param {Touch} point - Точка касания.
   * @returns {boolean} `true`, если точка находится в пределах canvas.
   */
  #isPointInsideCanvas(context, point) {
    const bounds = context.render.canvas.getBoundingClientRect()

    return (
      point.clientX >= bounds.left &&
      point.clientX <= bounds.right &&
      point.clientY >= bounds.top &&
      point.clientY <= bounds.bottom
    )
  }

  /**
   * Переводит координаты touch-события из viewport в координаты мира Matter.js.
   *
   * @param {Object} context - Текущий Matter.js context сцены.
   * @param {Touch} point - Точка касания.
   * @param {Matter.Mouse} mouse - Объект мыши Matter.js.
   * @returns {{x: number, y: number}} Координаты точки в физическом мире.
   */
  #getPointWorldPosition(context, point, mouse) {
    const { canvas } = context.render
    const bounds = canvas.getBoundingClientRect()
    const denominatorX = (bounds.width / canvas.width) * mouse.pixelRatio
    const denominatorY = (bounds.height / canvas.height) * mouse.pixelRatio

    return {
      x:
        ((point.clientX - bounds.left) / denominatorX) * mouse.scale.x +
        mouse.offset.x,
      y:
        ((point.clientY - bounds.top) / denominatorY) * mouse.scale.y +
        mouse.offset.y
    }
  }

  /**
   * Останавливает нативную обработку touch-события во время drag.
   *
   * @param {Event} event - DOM-событие.
   * @returns {void}
   */
  #stopTouchEvent(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Добавляет необходимые desktop-слушатели Matter Mouse.
   *
   * @param {Matter.Mouse} mouse - Объект мыши Matter.js.
   * @param {EventTarget} target - DOM-цель для событий Matter Mouse.
   * @returns {void}
   */
  #addMatterMouseElementListeners(mouse, target) {
    target.addEventListener('mousemove', mouse.mousemove)
    target.addEventListener('mousedown', mouse.mousedown)
    target.addEventListener('mouseup', mouse.mouseup)
  }

  /**
   * Удаляет все DOM-слушатели Matter Mouse при уничтожении context.
   *
   * @param {Matter.Mouse} mouse - Объект мыши Matter.js.
   * @param {EventTarget} target - DOM-цель, на которой Matter повесил события.
   * @returns {void}
   */
  #removeMatterMouseElementListeners(mouse, target) {
    target.removeEventListener('mousemove', mouse.mousemove)
    target.removeEventListener('mousedown', mouse.mousedown)
    target.removeEventListener('mouseup', mouse.mouseup)
    target.removeEventListener('wheel', mouse.mousewheel)
    target.removeEventListener('touchmove', mouse.mousemove)
    target.removeEventListener('touchstart', mouse.mousedown)
    target.removeEventListener('touchend', mouse.mouseup)
  }

  /**
   * Проверяет, что async-операция относится к текущему живому context.
   *
   * @param {Object} context - Проверяемый Matter.js context.
   * @returns {boolean} `true`, если context всё ещё актуален.
   */
  #isCurrentContext(context) {
    return Boolean(context && this.#context === context && !context.isDestroyed)
  }
}
